import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FeedbackService, PlusAuthenticationService } from '@karve.it/core';
import { User } from '@karve.it/interfaces/auth';
import { createSelector, Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppComponent } from 'src/app/app.component';
import { AppMainComponent } from 'src/app/app.main.component';
import { SubSink } from 'subsink';

import { environment } from '../../../environments/environment';
import { BaseZoneWithParentFragment } from '../../../generated/graphql.generated';
import { ColorSchemeService } from '../../color-scheme.service';
import { UNREAD_NOTIFICATIONS_COUNT_MAX } from '../../global.constants';
import { FRONTEND_PERMISSIONS } from '../../permissions.constants';
import { BrandingService } from '../../services/branding.service';
import { DemoModeService } from '../../services/demo-mode.service';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { NotificationsService } from '../../services/notifications.service';
import { PermissionService } from '../../services/permission.service';
import { HelpDialogComponent } from '../../shared/help-dialog/help-dialog.component';
import { createConfigSelector } from '../../state/branding.store';

const selectRingCentralEnabled = createSelector(
    createConfigSelector('RINGCENTRAL_ENABLED' as any),
    (enabled) => !!enabled,
);

const selectDialPadEnabled = createSelector(
    createConfigSelector('DIALPAD_ENABLED' as any),
    createConfigSelector('DIALPAD_CLIENTID' as any),
    (enabled, clientId) => !!enabled && !!clientId,
);

@Component({
  selector: 'app-topbar',
  templateUrl: './app.topbar.component.html',
  styleUrls: ['./app.topbar.component.scss'],
})
export class AppTopBarComponent implements OnInit, OnDestroy{

    @Input() sidebarStatic = false;
    @Input() sidebarMobileActive = false;

    @ViewChild(HelpDialogComponent) helpDialogRef: HelpDialogComponent;

    subs = new SubSink();

    items: MenuItem[];

    user: User;
    zone: BaseZoneWithParentFragment;

    inProduction = environment.production;

    frontendVersion = environment.version;

    ringCentralEnabled$: Observable<boolean>;
    dialPadEnabled$: Observable<boolean>;

    hasDemoPermission$: Observable<boolean>;

    showRingCentral = false;
    showDialpad = false;

    ringCentralUrl: SafeResourceUrl;
    dialPadUrl$: Observable<SafeResourceUrl>;

    constructor(
        private app: AppComponent,
        public appMain: AppMainComponent,
        public detailsHelper: DetailsHelperService,
        private plusAuth: PlusAuthenticationService,
        private brandingService: BrandingService,
        private router: Router,
        private feedbackSvc: FeedbackService,
        public colorSchemeService: ColorSchemeService,
        public notificationService: NotificationsService,
        private freyaHelper: FreyaHelperService,
    private store: Store,
    private sanitizer: DomSanitizer,
    public demoModeService: DemoModeService,
    private permissionService: PermissionService,
  ) {}

    ngOnInit() {
        this.updateUser();

        this.ringCentralUrl = this.sanitizer
            .bypassSecurityTrustResourceUrl(
                'https://ringcentral.github.io/ringcentral-embeddable/app.html'
            );

        this.ringCentralEnabled$ = this.store.select(selectRingCentralEnabled);

        this.dialPadEnabled$ = this.store.select(selectDialPadEnabled);

        this.dialPadUrl$ = this.store.select(createConfigSelector('DIALPAD_CLIENTID' as any)).pipe(
            filter((id): id is string => !!id),
            map((id) => this.sanitizer.bypassSecurityTrustResourceUrl(
                `https://dialpad.com/apps/${id}`
            ))
        );

        this.hasDemoPermission$ = this.permissionService.watchPermission(FRONTEND_PERMISSIONS.demoMode);

        this.subs.sink = this.brandingService.currentZone().subscribe((z) => {
            this.zone = z;
        });

        this.subs.sink = this.plusAuth.authState.subscribe((state) => {
            if (state === 'deauthenticating') {
                this.app.mutateContainerEnabled = false;
            } else if (state === 'deauthenticated') {
                this.app.mutateContainerEnabled = true;
            }
            // Refresh the user whenever auth state changes (e.g. after
            // bootstrapAuth completes and populates the user).
            this.updateUser();
        });

        this.subs.sink = this.freyaHelper.helpDialogOpened
            .subscribe(({ targetHelpId, sectionId }) => this.helpDialogRef.openToPage(targetHelpId, sectionId));
    }

    ngOnDestroy() {
        // if (this.subscription) {
        //     this.subscription.unsubscribe();
        // }
        this.subs.unsubscribe();
    }

    routeTo(route){
        this.router.navigate([route]);
    }

    logout(){
        return this.plusAuth.logout();
    }

    openFeedback(){
        this.feedbackSvc.openFeedbackDialog();
    }

    updateAppVersion() {
        location.reload();
    }

    toggleRingCentral() {
        this.showRingCentral = !this.showRingCentral;
    }

    toggleDialpad() {
        this.showDialpad = !this.showDialpad;
    }

    toggleDemoMode() {
        this.demoModeService.toggle();
    }

    get notificationsBadge() {
        const { unread } = this.notificationService;
        if (!unread) { return; }
        if (unread > UNREAD_NOTIFICATIONS_COUNT_MAX) {
            return `${UNREAD_NOTIFICATIONS_COUNT_MAX}+`;
        };
        return `${unread}`;
    }

    private updateUser() {
        this.user = this.plusAuth.user;
    }

    get displayName(): string {
        const name = (this.user?.givenName || '') + ' ' + (this.user?.familyName || '');
        return name.trim() || this.user?.email || '';
    }
}
